import { useState, useEffect } from 'react'
import { Download as DownloadIcon, Link as LinkIcon, CheckCircle as CheckCircleIcon, Monitor as MonitorIcon } from 'lucide-react'
import { connectionTokenApi, fetchHelperAppInfo, type ConnectionTokenResponse, type HelperAppInfo, BACKEND_URL } from '../lib/api'

function detectPlatform(): 'mac' | 'windows' {
  if (typeof navigator === 'undefined') return 'mac'
  return /Windows/i.test(navigator.userAgent) ? 'windows' : 'mac'
}

function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false
  const isMobileWidth = window.innerWidth < 768
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  return isMobileWidth || isMobileUA
}

const AppleIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
)

const WindowsIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 12V6.5l8-1.1v6.6H3zm0 5.5V12h8v6.6l-8-1.1zm9-11.1l9-1.3V12h-9V6.4zm0 11.2V12h9v6.9l-9-1.3z"/>
  </svg>
)

interface HelperAppDownloadProps {
  /** Called after connect deep link is opened (e.g. advance onboarding) */
  onConnected?: () => void
}

export default function HelperAppDownload({ onConnected }: HelperAppDownloadProps) {
  const [detectedPlatform, setDetectedPlatform] = useState<'mac' | 'windows'>('mac')
  const [connectionToken, setConnectionToken] = useState<ConnectionTokenResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [appInfo, setAppInfo] = useState<HelperAppInfo | null>(null)

  useEffect(() => {
    setDetectedPlatform(detectPlatform())
    setIsMobile(isMobileDevice())
    const onResize = () => setIsMobile(isMobileDevice())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    fetchHelperAppInfo().then(setAppInfo).catch(() => {})
  }, [])

  useEffect(() => {
    if (!connectionToken) {
      setTimeRemaining(null)
      return
    }
    const updateTimer = () => {
      const expiresAt = new Date(connectionToken.expiresAt).getTime()
      const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
      setTimeRemaining(remaining)
      if (remaining === 0) setConnectionToken(null)
    }
    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [connectionToken])

  const handleDownload = (platform: 'mac' | 'windows') => {
    const platformParam = platform === 'windows' ? '?platform=windows' : ''
    window.open(`${BACKEND_URL}/api/v1/downloads/helper-app${platformParam}`, '_blank')
  }

  const openDeepLink = (token: string) => {
    const encodedToken = encodeURIComponent(token)
    const link = document.createElement('a')
    link.href = `unbreakable://connect?token=${encodedToken}`
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleConnect = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await connectionTokenApi.generateConnectionToken()
      setConnectionToken(token)
      openDeepLink(token.token)
      onConnected?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate connection token')
    } finally {
      setLoading(false)
    }
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (isMobile) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 text-center space-y-6">
        <div className="w-20 h-20 rounded-xl bg-brand-light flex items-center justify-center mx-auto">
          <MonitorIcon size={40} className="text-brand" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Desktop Required</h2>
          <p className="text-gray-600 mb-4">The Helper App setup must be completed on a desktop or laptop computer.</p>
          <p className="text-sm text-gray-500">Please visit this page on your Mac or Windows computer.</p>
        </div>
      </div>
    )
  }

  const macInfo = appInfo?.mac
  const winInfo = appInfo?.windows

  return (
    <div className="space-y-8">
      {/* Download section */}
      <div>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Download Helper App</h2>
          <p className="text-gray-600">
            Install the Unbreakable Helper App on your computer to manage connected devices.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* macOS card */}
          <div className={`rounded-xl border-2 p-6 transition-all ${detectedPlatform === 'mac' ? 'border-brand bg-brand-light' : 'border-gray-200 bg-white'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${detectedPlatform === 'mac' ? 'bg-white' : 'bg-gray-100'}`}>
                <AppleIcon />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">macOS</h3>
                <p className="text-xs text-gray-500">
                  {macInfo ? `${macInfo.version} \u00b7 ${macInfo.minOS}` : 'macOS 14.0+'}
                </p>
              </div>
              {detectedPlatform === 'mac' && (
                <span className="ml-auto text-xs font-medium text-brand bg-white px-2 py-1 rounded-full">Detected</span>
              )}
            </div>
            <button
              onClick={() => handleDownload('mac')}
              className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                detectedPlatform === 'mac'
                  ? 'bg-brand text-white hover:bg-brand-hover'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <DownloadIcon size={16} />
              Download for Mac
            </button>
          </div>

          {/* Windows card */}
          <div className={`rounded-xl border-2 p-6 transition-all ${detectedPlatform === 'windows' ? 'border-brand bg-brand-light' : 'border-gray-200 bg-white'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${detectedPlatform === 'windows' ? 'bg-white' : 'bg-gray-100'}`}>
                <WindowsIcon />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Windows</h3>
                <p className="text-xs text-gray-500">
                  {winInfo ? `${winInfo.version} \u00b7 ${winInfo.minOS}` : 'Windows 10+'}
                </p>
              </div>
              {detectedPlatform === 'windows' && (
                <span className="ml-auto text-xs font-medium text-brand bg-white px-2 py-1 rounded-full">Detected</span>
              )}
            </div>
            <button
              onClick={() => handleDownload('windows')}
              className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                detectedPlatform === 'windows'
                  ? 'bg-brand text-white hover:bg-brand-hover'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <DownloadIcon size={16} />
              Download for Windows
            </button>
          </div>
        </div>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">
            {detectedPlatform === 'mac'
              ? 'Open the .dmg file, then drag Unbreakable to Applications.'
              : 'Run the .msi installer and follow the setup wizard.'}
          </p>
        </div>
      </div>

      {/* Connect section */}
      <div className="border-t pt-8">
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Connect Your Account</h2>
          <p className="text-gray-600">
            After installing, click below to authenticate the helper app with your account.
          </p>
        </div>

        <div className="max-w-md mx-auto">
          {connectionToken && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 mb-4">
              <CheckCircleIcon size={20} className="text-green-500" />
              <div>
                <p className="font-medium text-green-800 text-sm">Token generated</p>
                {timeRemaining !== null && (
                  <p className="text-xs text-green-600">Expires in {formatTime(timeRemaining)}</p>
                )}
              </div>
            </div>
          )}

          <button
            onClick={connectionToken ? () => { openDeepLink(connectionToken.token); onConnected?.() } : handleConnect}
            disabled={loading}
            className="w-full py-3 bg-brand text-white rounded-xl font-medium hover:bg-brand-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? 'Connecting...' : (
              <>
                <LinkIcon size={18} />
                {connectionToken ? 'Open in Helper App' : 'Connect Helper App'}
              </>
            )}
          </button>
          <p className="text-xs text-gray-400 mt-3 text-center">
            Generates a secure token and opens the Unbreakable helper app.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
